import type { ReadonlyURLSearchParams } from 'next/navigation';
import { isValidActivity } from '@alltrails/explore-service/hooks/filterConfigs/useActivityFilterConfig';
import { getValidDuration } from '@alltrails/explore-service/hooks/filterConfigs/useDurationFilterConfig';
import { getValidElevationGain } from '@alltrails/explore-service/hooks/filterConfigs/useElevationGainFilterConfig';
import { getValidHighestPoint } from '@alltrails/explore-service/hooks/filterConfigs/useHighestPointFilterConfig';
import { getValidLength } from '@alltrails/explore-service/hooks/filterConfigs/useLengthFilterConfig';
import { isValidRating } from '@alltrails/explore-service/hooks/filterConfigs/useMinRatingFilterConfig';
import { getValidRouteTypes } from '@alltrails/explore-service/hooks/filterConfigs/useRouteTypeFilterConfig';
import { getValidSuitabilities } from '@alltrails/explore-service/hooks/filterConfigs/useSuitabilityFilterConfig';
import type ExploreServiceFilters from '@alltrails/explore-service/types/ExploreServiceFilters';
import { getValidDifficulties } from '@alltrails/explore-service/utils/getValidDifficulties';
import { getValidFeatures } from '@alltrails/explore-service/utils/getValidFeatures';
import {
  activityParamKey,
  difficultyParamKey,
  durationParamKey,
  elevationGainParamKey,
  featureParamKey,
  highestPointParamKey,
  lengthParamKey,
  minRatingParamKey,
  routeTypeParamKey,
  suitabilityParamKey,
  trailTrafficParamKey,
  trailTrafficParamValues
} from '@alltrails/filters/utils/exploreSearchParams';
import type TrailTraffic from '@alltrails/trails/types/TrailTraffic';

export const filterParamKeys = [
  activityParamKey,
  difficultyParamKey,
  durationParamKey,
  elevationGainParamKey,
  featureParamKey,
  highestPointParamKey,
  lengthParamKey,
  minRatingParamKey,
  routeTypeParamKey,
  suitabilityParamKey,
  trailTrafficParamKey
];

export const parseFiltersFromSearchParams = (searchParams: ReadonlyURLSearchParams, useMetric: boolean): ExploreServiceFilters => {
  const filters: ExploreServiceFilters = {};

  const activity = searchParams.get(activityParamKey);
  if (activity && isValidActivity(activity)) {
    filters.activity = [activity];
  }

  const difficulties = getValidDifficulties(searchParams.getAll(difficultyParamKey) || []);
  if (difficulties.length) {
    filters.difficulty = difficulties;
  }

  const lengths = searchParams.getAll(lengthParamKey) || [];
  if (lengths.length === 2) {
    const validMin = lengths[0] === '0' ? undefined : getValidLength(Number(lengths[0]), useMetric);
    const validMax = lengths[1] === '-1' ? undefined : getValidLength(Number(lengths[1]), useMetric);
    if (validMin && !validMax) {
      filters.length = { min: validMin };
    } else if (!validMin && validMax) {
      filters.length = { max: validMax };
    } else if (validMin && validMax) {
      filters.length = { min: validMin, max: validMax };
    }
  }

  const durations = searchParams.getAll(durationParamKey) || [];
  if (durations.length === 2) {
    const validMin = durations[0] === '0' ? undefined : getValidDuration(Number(durations[0]));
    const validMax = durations[1] === '-1' ? undefined : getValidDuration(Number(durations[1]));
    if (validMin && !validMax) {
      filters.duration = { min: validMin };
    } else if (!validMin && validMax) {
      filters.duration = { max: validMax };
    } else if (validMin && validMax) {
      filters.duration = { min: validMin, max: validMax };
    }
  }

  const elevations = searchParams.getAll(elevationGainParamKey) || [];
  if (elevations.length === 2) {
    const validMin = elevations[0] === '0' ? undefined : getValidElevationGain(Number(elevations[0]), useMetric);
    const validMax = elevations[1] === '-1' ? undefined : getValidElevationGain(Number(elevations[1]), useMetric);
    if (validMin && !validMax) {
      filters.elevationGain = { min: validMin };
    } else if (!validMin && validMax) {
      filters.elevationGain = { max: validMax };
    } else if (validMin && validMax) {
      filters.elevationGain = { min: validMin, max: validMax };
    }
  }

  const suitabilities = getValidSuitabilities(searchParams.getAll(suitabilityParamKey) || []);
  if (suitabilities.length) {
    filters.suitability = suitabilities;
  }

  const features = getValidFeatures(searchParams.getAll(featureParamKey) || []);
  if (features.length) {
    filters.feature = features;
  }

  const routeTypes = getValidRouteTypes(searchParams.getAll(routeTypeParamKey) || []);
  if (routeTypes.length) {
    filters.routeType = routeTypes;
  }

  const minRating = searchParams.get(minRatingParamKey);
  if (minRating && isValidRating(Number(minRating))) {
    filters.minRating = Number(minRating);
  }

  const trailTrafficValues = searchParams.getAll(trailTrafficParamKey) || [];
  const trailTraffics = (Object.keys(trailTrafficParamValues) as TrailTraffic[]).filter(key =>
    trailTrafficValues.includes(trailTrafficParamValues[key])
  );
  if (trailTraffics.length) {
    filters.trailTraffic = trailTraffics;
  }

  const highestPoints = searchParams.getAll(highestPointParamKey) || [];
  if (highestPoints.length === 2) {
    const validMin = highestPoints[0] === '0' ? undefined : getValidHighestPoint(Number(highestPoints[0]), useMetric);
    const validMax = highestPoints[1] === '-1' ? undefined : getValidHighestPoint(Number(highestPoints[1]), useMetric);
    if (validMin && !validMax) {
      filters.highestPoint = { min: validMin };
    } else if (!validMin && validMax) {
      filters.highestPoint = { max: validMax };
    } else if (validMin && validMax) {
      filters.highestPoint = { min: validMin, max: validMax };
    }
  }

  return filters;
};

export const serializeFiltersToSearchParams = (filters: ExploreServiceFilters | undefined, params: URLSearchParams): void => {
  if (!filters) {
    return;
  }

  if (filters.activity?.length) {
    params.append(activityParamKey, filters.activity[0]);
  }
  if (filters.difficulty) {
    filters.difficulty.forEach(diff => params.append(difficultyParamKey, diff));
  }
  if (filters.length) {
    const { min, max } = filters.length;
    params.append(lengthParamKey, String(min ?? 0));
    params.append(lengthParamKey, String(max ?? -1));
  }
  if (filters.duration) {
    const { min, max } = filters.duration;
    params.append(durationParamKey, String(min ?? 0));
    params.append(durationParamKey, String(max ?? -1));
  }
  if (filters.elevationGain) {
    const { min, max } = filters.elevationGain;
    params.append(elevationGainParamKey, String(min ?? 0));
    params.append(elevationGainParamKey, String(max ?? -1));
  }
  if (filters.suitability) {
    filters.suitability.forEach(s => params.append(suitabilityParamKey, s));
  }
  if (filters.feature) {
    filters.feature.forEach(f => params.append(featureParamKey, f));
  }
  if (filters.routeType) {
    filters.routeType.forEach(r => params.append(routeTypeParamKey, r));
  }
  if (filters.minRating) {
    params.append(minRatingParamKey, String(filters.minRating));
  }
  if (filters.trailTraffic) {
    filters.trailTraffic.forEach(t => params.append(trailTrafficParamKey, trailTrafficParamValues[t]));
  }
  if (filters.highestPoint) {
    const { min, max } = filters.highestPoint;
    params.append(highestPointParamKey, String(min ?? 0));
    params.append(highestPointParamKey, String(max ?? -1));
  }
};
